import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const CheckoutPage = ({ language }) => {
  const navigate = useNavigate();
  const [cart, setCart] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    pincode: '',
    paymentMethod: 'razorpay'
  });
  const [loading, setLoading] = useState(false);
  const API = process.env.REACT_APP_BACKEND_URL;
  const API_BASE = API && API.endsWith('/api') ? API : `${API}/api`;

  useEffect(() => {
    const savedCart = localStorage.getItem('cart');
    if (savedCart) {
      setCart(JSON.parse(savedCart));
    } else {
      navigate('/cart');
    }
  }, [navigate]);

  const getTotal = () => {
    return cart.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const getTotalWithTax = () => {
    return Math.round(getTotal() * 1.18);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Server-side PayPal flow: create order on backend and redirect to PayPal approval
      if (formData.paymentMethod === 'paypal') {
        const amountUSD = (getTotalWithTax() / 80).toFixed(2); // quick INR->USD approx for sandbox
        const createRes = await axios.post(`${API_BASE}/payments/paypal/create-order`, {
          amount: parseFloat(amountUSD),
          currency: 'USD',
          order_id: `order_${Date.now()}`
        });

        if (createRes.data && createRes.data.approval_url) {
          window.location.href = createRes.data.approval_url;
          return;
        } else {
          throw new Error('No approval URL returned from server');
        }
      }
      if (formData.paymentMethod === 'razorpay') {
        // Create Razorpay order
        const orderRes = await axios.post(`${API_BASE}/payments/razorpay/create-order`, {
          amount: getTotalWithTax(),
          currency: 'INR',
          receipt: `order_${Date.now()}`,
          notes: { customer: formData.name }
        });

        const options = {
          key: orderRes.data.key_id,
          amount: orderRes.data.amount,
          currency: orderRes.data.currency,
          name: 'Ahobilam.com',
          description: 'Order Payment',
          order_id: orderRes.data.order_id,
          handler: async function (response) {
            // Verify payment
            const verifyRes = await axios.post(`${API_BASE}/payments/razorpay/verify-payment`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature
            });

            if (verifyRes.data.success) {
              // Create order
              await axios.post(`${API_BASE}/shop/orders`, {
                items: cart.map(item => ({
                  product_id: item.id,
                  product_name: item.name,
                  quantity: item.quantity,
                  price: item.price
                })),
                total_amount: getTotalWithTax(),
                payment_method: 'razorpay',
                payment_status: 'completed',
                payment_id: response.razorpay_payment_id,
                shipping_address: formData
              });

              localStorage.removeItem('cart');
              alert(language === 'en' ? 'Order placed successfully!' : 'ஆர்டர் வெற்றிகரமாக வைக்கப்பட்டது!');
              navigate('/');
            }
          },
          prefill: {
            name: formData.name,
            email: formData.email,
            contact: formData.phone
          },
          theme: { color: '#16a34a' }
        };

        const rzp = new window.Razorpay(options);
        rzp.open();
      }
    } catch (error) {
      console.error('Payment error:', error);
      alert('Payment failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-orange-50 py-12 px-4">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-4xl font-bold text-green-800 mb-8">
          {language === 'en' ? 'Checkout' : 'செக்அவுட்'}
        </h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">
                {language === 'en' ? 'Shipping Details' : 'ஷிப்பிங் விவரங்கள்'}
              </h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input
                    type="text"
                    placeholder={language === 'en' ? 'Full Name' : 'முழு பெயர்'}
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500"
                    required
                  />
                  <input
                    type="email"
                    placeholder={language === 'en' ? 'Email' : 'மின்னஞ்சல்'}
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500"
                    required
                  />
                </div>
                <input
                  type="tel"
                  placeholder={language === 'en' ? 'Phone Number' : 'தொலைபேசி எண்'}
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500"
                  required
                />
                <textarea
                  placeholder={language === 'en' ? 'Address' : 'முகவரி'}
                  value={formData.address}
                  onChange={(e) => setFormData({...formData, address: e.target.value})}
                  className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500"
                  rows="3"
                  required
                />
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <input
                    type="text"
                    placeholder={language === 'en' ? 'City' : 'நகரம்'}
                    value={formData.city}
                    onChange={(e) => setFormData({...formData, city: e.target.value})}
                    className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500"
                    required
                  />
                  <input
                    type="text"
                    placeholder={language === 'en' ? 'State' : 'மாநிலம்'}
                    value={formData.state}
                    onChange={(e) => setFormData({...formData, state: e.target.value})}
                    className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500"
                    required
                  />
                  <input
                    type="text"
                    placeholder={language === 'en' ? 'Pincode' : 'பின் குறியீடு'}
                    value={formData.pincode}
                    onChange={(e) => setFormData({...formData, pincode: e.target.value})}
                    className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500"
                    required
                  />
                </div>

                <div className="pt-4">
                  <h3 className="text-lg font-bold mb-4">
                    {language === 'en' ? 'Payment Method' : 'கட்டண முறை'}
                  </h3>
                  <div className="space-y-2">
                    <label className="flex items-center p-4 border rounded-lg cursor-pointer hover:bg-green-50">
                      <input
                        type="radio"
                        name="paymentMethod"
                        value="razorpay"
                        checked={formData.paymentMethod === 'razorpay'}
                        onChange={(e) => setFormData({...formData, paymentMethod: e.target.value})}
                        className="mr-3"
                      />
                      <span className="font-semibold">Razorpay (UPI, Cards, NetBanking)</span>
                    </label>
                    <label className="flex items-center p-4 border rounded-lg cursor-pointer hover:bg-orange-50">
                      <input
                        type="radio"
                        name="paymentMethod"
                        value="upi"
                        checked={formData.paymentMethod === 'upi'}
                        onChange={(e) => setFormData({...formData, paymentMethod: e.target.value})}
                        className="mr-3"
                      />
                      <span className="font-semibold">💳 UPI/BHIM QR Code (Direct)</span>
                    </label>
                    <label className="flex items-center p-4 border rounded-lg cursor-pointer hover:bg-blue-50">
                      <input
                        type="radio"
                        name="paymentMethod"
                        value="paypal"
                        checked={formData.paymentMethod === 'paypal'}
                        onChange={(e) => setFormData({...formData, paymentMethod: e.target.value})}
                        className="mr-3"
                      />
                      <span className="font-semibold">PayPal</span>
                    </label>
                  </div>
                  
                  {/* Show UPI QR Code if selected */}
                  {formData.paymentMethod === 'upi' && (
                    <div className="mt-4 bg-gradient-to-br from-orange-50 to-green-50 rounded-lg p-6 text-center">
                      <p className="text-lg font-semibold text-gray-800 mb-4">
                        {language === 'en' ? 'Scan QR Code to Pay' : 'செலுத்த QR குறியீட்டை ஸ்கேன் செய்யவும்'}
                      </p>
                      <img 
                        src="/bhim-qr.jpg" 
                        alt="UPI QR Code" 
                        className="mx-auto max-w-xs w-full rounded-lg shadow-lg mb-3"
                      />
                      <div className="bg-white rounded-lg p-3 inline-block mb-3">
                        <p className="text-sm text-gray-600">UPI ID:</p>
                        <p className="text-lg font-mono font-bold text-green-700">svknvs@okicici</p>
                      </div>
                      <p className="text-sm text-gray-600">
                        {language === 'en' 
                          ? 'After payment, please send screenshot to confirm your order' 
                          : 'கட்டணத்திற்குப் பிறகு, உங்கள் ஆர்டரை உறுதிப்படுத்த ஸ்கிரீன்ஷாட் அனுப்பவும்'}
                      </p>
                    </div>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-green-600 text-white py-4 rounded-lg hover:bg-green-700 font-bold text-lg disabled:bg-gray-400"
                >
                  {loading ? 'Processing...' : (language === 'en' ? 'Place Order' : 'ஆர்டர் செய்யவும்')}
                </button>
              </form>
            </div>
          </div>

          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-md p-6 sticky top-8">
              <h3 className="text-xl font-bold mb-4">
                {language === 'en' ? 'Order Summary' : 'ஆர்டர் சுருக்கம்'}
              </h3>
              <div className="space-y-2 mb-4">
                {cart.map(item => (
                  <div key={item.id} className="flex justify-between text-sm">
                    <span>{language === 'en' ? item.name : item.name_ta} x{item.quantity}</span>
                    <span>₹{item.price * item.quantity}</span>
                  </div>
                ))}
              </div>
              <div className="border-t pt-4 space-y-2">
                <div className="flex justify-between">
                  <span>{language === 'en' ? 'Subtotal' : 'மொத்தம்'}:</span>
                  <span>₹{getTotal()}</span>
                </div>
                <div className="flex justify-between">
                  <span>{language === 'en' ? 'Tax (18%)' : 'வரி (18%)'}:</span>
                  <span>₹{Math.round(getTotal() * 0.18)}</span>
                </div>
                <div className="flex justify-between text-xl font-bold text-green-600">
                  <span>{language === 'en' ? 'Total' : 'மொத்தம்'}:</span>
                  <span>₹{getTotalWithTax()}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;